import React, {useEffect, useState} from "react";
import {Spinner} from "@heroui/react";
import {getErrorMessage, toastError, toastSuccess} from "../../lib/toast";
import Header from "../../components/Private/Header";
import {useMutation, useQuery} from "@tanstack/react-query";
import {request} from "../../lib/request";
import {Link} from "react-router";

const DISCOUNT_TYPES = {
    tam_dar: {
        title: "پیشنهاد طعم‌دار",
        description:
            "تخفیف روی بخشی از منو (مثلاً فقط ساندویچ یا دو مدل پیتزا). می‌توانید تخفیف را در منوی محصولات اعمال کنید یا جزئیات را اینجا بنویسید تا پشتیبانی ثبت کند.",
        placeholder: "مثال: ۱۵٪ تخفیف روی دسته ساندویچ تا پایان هفته",
        hint: "ویرایش مستقیم تخفیف: منوی محصولات",
        hintLink: "/products-menu",
    },
    khosh: {
        title: "تخفیف خوشمزه",
        description:
            "تخفیف روی کل منو با درصد مشخص. رستوران شما در بخش «تخفیف‌های خوشمزه» سایت نمایش داده می‌شود.",
        placeholder: "مثال: ۱۰٪ تخفیف روی کل منو",
    },
    first: {
        title: "تخفیف اولین سفارش",
        description:
            "تخفیف ویژه برای مشتریانی که برای اولین بار از رستوران شما سفارش می‌دهند.",
        placeholder: "مثال: ۲۰٪ تخفیف اولین سفارش",
    },
    code: {
        title: "کد تخفیف",
        description:
            "تعریف کد تخفیف اختصاصی برای مشتریان حضوری یا کمپین‌های خاص.",
        placeholder: "مثال: کد OFF20 با ۲۰٪ تخفیف تا سقف ۵۰ هزار تومان",
    },
    send: {
        title: "ارسال رایگان",
        description:
            "هزینه ارسال سفارش‌ها را رستوران تقبل می‌کند. با فعال‌سازی، درخواست بلافاصله ثبت می‌شود.",
        instant: true,
    },
};

const EMPTY_FORM = {
    tam_dar: {enabled: false, text: ""},
    khosh: {enabled: false, text: ""},
    first: {enabled: false, text: ""},
    code: {enabled: false, text: ""},
    send: {enabled: false, text: ""},
};

/** API may return boolean true or integer 1 for active discount flags. */
function isDiscountEnabled(value) {
    return value === true || value === 1 || value === "1";
}

function DiscountToggle({checked, disabled, onChange, label}) {
    return (
        <label className={`rest-discount-toggle${disabled ? " rest-discount-toggle--disabled" : ""}`}>
            <input
                type="checkbox"
                className="rest-discount-toggle__input"
                checked={checked}
                disabled={disabled}
                onChange={(e) => onChange(e.target.checked)}
            />
            <span className="rest-discount-toggle__track" aria-hidden="true">
                <span className="rest-discount-toggle__thumb"/>
            </span>
            <span className="sr-only">{label}</span>
        </label>
    );
}

function DiscountCard({
    type,
    meta,
    enabled,
    text,
    loading,
    submitLocked,
    onToggle,
    onTextChange,
    onSubmit,
}) {
    const isInstant = Boolean(meta.instant);
    const submitDisabled = !enabled || loading || submitLocked;

    return (
        <article className={`rest-discount-card${enabled ? " rest-discount-card--active" : ""}`}>
            <div className="rest-discount-card__accent" aria-hidden="true"/>

            <header className="rest-discount-card__header">
                <div className="rest-discount-card__heading">
                    <div className="rest-discount-card__title-row">
                        <h2 className="rest-discount-card__title">{meta.title}</h2>
                        <span className={`rest-discount-card__status${enabled ? " is-on" : ""}`}>
                            {enabled ? "فعال" : "غیرفعال"}
                        </span>
                    </div>
                    <p className="rest-discount-card__desc">{meta.description}</p>
                    {meta.hint && meta.hintLink && (
                        <Link to={meta.hintLink} className="rest-discount-card__link">
                            {meta.hint}
                        </Link>
                    )}
                </div>

                <DiscountToggle
                    checked={enabled}
                    disabled={loading}
                    onChange={onToggle}
                    label={meta.title}
                />
            </header>

            {!isInstant && (
                <div className={`rest-discount-card__body${enabled ? "" : " rest-discount-card__body--disabled"}`}>
                    <label className="rest-discount-field__label" htmlFor={`discount-text-${type}`}>
                        توضیحات درخواست
                    </label>
                    <textarea
                        id={`discount-text-${type}`}
                        className="rest-discount-field__textarea"
                        value={text}
                        disabled={!enabled || loading || submitLocked}
                        onChange={(e) => onTextChange(e.target.value)}
                        placeholder={meta.placeholder}
                        rows={3}
                    />
                    <button
                        type="button"
                        className="rest-discount-card__submit"
                        disabled={submitDisabled}
                        onClick={onSubmit}
                    >
                        {loading ? <Spinner size="sm" color="white" variant="dots"/> : submitLocked ? "درخواست ثبت شده" : "ثبت درخواست"}
                    </button>
                </div>
            )}

            {isInstant && loading && (
                <div className="rest-discount-card__instant-loading">
                    <Spinner size="sm" color="warning" variant="dots"/>
                    <span>در حال ثبت...</span>
                </div>
            )}
        </article>
    );
}

const Off = () => {
    const [pendingType, setPendingType] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [submittedTypes, setSubmittedTypes] = useState({});
    const [feedback, setFeedback] = useState(null);

    const showFeedback = (type, message) => {
        setFeedback({type, message});
        window.scrollTo({top: 0, behavior: "smooth"});
        if (type === "success") {
            toastSuccess(message);
        } else {
            toastError(message);
        }
    };

    const {data, isLoading, isError, error, refetch} = useQuery({
        queryKey: ["offers"],
        queryFn: async () =>
            await request({
                url: "/restaurant/discount",
                method: "get",
            }),
        staleTime: 1000 * 60,
    });

    useEffect(() => {
        if (!data?.data) return;

        const payload = data.data;
        setForm({
            tam_dar: {
                enabled: isDiscountEnabled(payload.tam_dar_status),
                text: payload.tam_dar_text || "",
            },
            khosh: {
                enabled: isDiscountEnabled(payload.khosh_status),
                text: payload.khosh_text || "",
            },
            first: {
                enabled: isDiscountEnabled(payload.first_status),
                text: payload.first_text || "",
            },
            code: {
                enabled: isDiscountEnabled(payload.code_status),
                text: payload.code_text || "",
            },
            send: {
                enabled: isDiscountEnabled(payload.send_status),
                text: "",
            },
        });
        setSubmittedTypes({
            tam_dar: isDiscountEnabled(payload.tam_dar_status),
            khosh: isDiscountEnabled(payload.khosh_status),
            first: isDiscountEnabled(payload.first_status),
            code: isDiscountEnabled(payload.code_status),
            send: isDiscountEnabled(payload.send_status),
        });
    }, [data]);

    const {mutate} = useMutation({
        mutationFn: async (payload) =>
            await request({
                url: "/restaurant/discount/store",
                method: "post",
                data: payload,
            }),
        onMutate: ({type}) => setPendingType(type),
        onSuccess: (response, variables) => {
            const message = response.message || "درخواست ثبت شد";
            showFeedback("success", message);

            const payload = response?.data;
            const isActive = Boolean(variables.status);

            if (payload && typeof payload === "object") {
                setForm((prev) => ({
                    ...prev,
                    [variables.type]: {
                        enabled: isDiscountEnabled(payload[`${variables.type}_status`]),
                        text:
                            variables.type === "send" || !isActive
                                ? ""
                                : payload[`${variables.type}_text`] || "",
                    },
                }));
            } else {
                updateField(variables.type, {
                    enabled: isActive,
                    text: isActive ? variables.text || "" : "",
                });
            }

            // Keep submit locked while discount stays active.
            setSubmittedTypes((prev) => ({
                ...prev,
                [variables.type]: isActive,
            }));
            refetch();
        },
        onError: (err) => showFeedback("error", getErrorMessage(err)),
        onSettled: () => setPendingType(null),
    });

    const updateField = (type, patch) => {
        setForm((prev) => ({
            ...prev,
            [type]: {...prev[type], ...patch},
        }));
    };

    const submitType = (type, enabledOverride, textOverride) => {
        const section = form[type];
        mutate({
            type,
            status: enabledOverride ?? section.enabled,
            text: textOverride ?? section.text,
        });
    };

    const handleToggle = (type, enabled) => {
        const meta = DISCOUNT_TYPES[type];

        if (!enabled) {
            // Only ask API to deactivate when a discount was already registered/active.
            // Locally turning the toggle on (without «ثبت درخواست») must not create
            // a deactivation request if the user turns it back off.
            const wasAlreadyActive = Boolean(submittedTypes[type]);
            updateField(type, {enabled: false, text: ""});
            setSubmittedTypes((prev) => ({...prev, [type]: false}));
            if (wasAlreadyActive) {
                submitType(type, false, "");
            }
            return;
        }

        // Turning on only unlocks the form; status stays off until «ثبت درخواست»
        // (except instant types like free shipping which submit immediately).
        updateField(type, {enabled: true});
        setSubmittedTypes((prev) => ({...prev, [type]: false}));

        if (meta.instant) {
            submitType(type, true, "");
        }
    };

    const handleSubmit = (type) => {
        const section = form[type];
        if (!section?.enabled || submittedTypes[type] || pendingType === type) {
            return;
        }
        if (type !== "send" && !String(section.text || "").trim()) {
            showFeedback("error", "لطفاً توضیحات درخواست را وارد کنید.");
            return;
        }
        submitType(type, true, section.text);
    };

    const activeCount = Object.entries(form).filter(([, value]) => value.enabled).length;

    return (
        <>
            <Header title="تخفیف‌ها"/>

            {feedback && (
                <div
                    className={`rest-menu-save-feedback rest-menu-save-feedback--${feedback.type}`}
                    role="alert"
                >
                    {feedback.message}
                </div>
            )}

            <section className="rest-discount-page">
                <div className="rest-discount-intro">
                    <p className="rest-discount-intro__text">
                        نوع تخفیف مورد نظر را فعال کنید، توضیحات را بنویسید و درخواست را ثبت کنید.
                        پس از بررسی، توسط تیم غذارسان اعمال می‌شود.
                    </p>
                    {!isLoading && !isError && (
                        <p className="rest-discount-intro__meta">
                            {activeCount.toLocaleString("fa-IR")} تخفیف فعال
                        </p>
                    )}
                </div>

                {isLoading ? (
                    <div className="rest-orders-state rest-orders-state--loading">
                        <Spinner color="warning"/>
                        <p>در حال بارگذاری تنظیمات تخفیف...</p>
                    </div>
                ) : isError ? (
                    <div className="rest-orders-state rest-orders-state--empty">
                        <p className="rest-orders-state__title">بارگذاری ناموفق</p>
                        <p className="rest-orders-state__text">
                            {error?.response?.data?.message || error?.message || "خطا در دریافت اطلاعات تخفیف"}
                        </p>
                        <button type="button" className="rest-discount-card__submit" onClick={() => refetch()}>
                            تلاش مجدد
                        </button>
                    </div>
                ) : (
                    <div className="rest-discount-list">
                        {Object.entries(DISCOUNT_TYPES).map(([type, meta]) => (
                            <DiscountCard
                                key={type}
                                type={type}
                                meta={meta}
                                enabled={form[type].enabled}
                                text={form[type].text}
                                loading={pendingType === type}
                                submitLocked={submittedTypes[type]}
                                onToggle={(enabled) => handleToggle(type, enabled)}
                                onTextChange={(text) => {
                                    if (submittedTypes[type]) {
                                        return;
                                    }
                                    updateField(type, {text});
                                }}
                                onSubmit={() => handleSubmit(type)}
                            />
                        ))}

                        <article className="rest-discount-card rest-discount-card--info">
                            <div className="rest-discount-card__accent" aria-hidden="true"/>

                            <header className="rest-discount-card__header">
                                <div className="rest-discount-card__heading">
                                    <div className="rest-discount-card__title-row">
                                        <h2 className="rest-discount-card__title">لحظه‌های داغ</h2>
                                        <span className="rest-discount-card__status">راهنما</span>
                                    </div>
                                    <p className="rest-discount-card__desc">
                                        این نوع تخفیف غذای تخفیف‌دار شما را فقط تا پایان روز جاری در صفحه اصلی سایت قرار می‌دهد
                                        و برای فردای آن روز تخفیف به‌صورت خودکار برداشته می‌شود. برای فعال کردن باید از طریق
                                        منو محصولات و صفحه ویرایش غذا، تخفیف را لحاظ و گزینه لحظه‌های داغ را انتخاب کنید.
                                    </p>
                                </div>
                            </header>
                        </article>
                    </div>
                )}
            </section>
        </>
    );
};

export default Off;
